export const MAX_GPX_FILE_BYTES = 5 * 1024 * 1024;
export const MAX_ROUTE_POINTS = 2_000;

export type RouteCoordinate = readonly [lng: number, lat: number, elevation?: number];

export type RouteBounds = Readonly<{
  maxLat: number;
  maxLng: number;
  minLat: number;
  minLng: number;
}>;

export type RouteLineString = Readonly<{
  coordinates: readonly RouteCoordinate[];
  type: 'LineString';
}>;

export type ParsedGpxRoute = Readonly<{
  bounds: RouteBounds;
  distanceM: number;
  elevationGainM: number | null;
  geometry: RouteLineString;
  name: string | null;
  originalPointCount: number;
}>;

type RawRoutePoint = Readonly<{
  elevation: number | null;
  lat: number;
  lng: number;
}>;

const EARTH_RADIUS_M = 6_371_000;

function radians(value: number): number {
  return (value * Math.PI) / 180;
}

function distanceMeters(a: RawRoutePoint, b: RawRoutePoint): number {
  const latDelta = radians(b.lat - a.lat);
  const lngDelta = radians(b.lng - a.lng);
  const startLat = radians(a.lat);
  const endLat = radians(b.lat);
  const haversine =
    Math.sin(latDelta / 2) ** 2 +
    Math.cos(startLat) * Math.cos(endLat) * Math.sin(lngDelta / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(haversine));
}

function xmlEntityDecode(value: string): string {
  return value
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&quot;', '"')
    .replaceAll('&apos;', "'")
    .replaceAll('&amp;', '&');
}

function xmlEntityEncode(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}

function routeName(xml: string): string | null {
  const match = /<(?:\w+:)?name\b[^>]*>([^<]*)<\/(?:\w+:)?name>/i.exec(xml);
  const normalized = xmlEntityDecode(match?.[1] ?? '').trim();
  return normalized || null;
}

function attribute(attributes: string, name: string): string | null {
  const match = new RegExp(`\\b${name}\\s*=\\s*["']([^"']*)["']`, 'i').exec(attributes);
  return match?.[1] ?? null;
}

function elevation(body: string): number | null {
  const raw = /<(?:\w+:)?ele\b[^>]*>([^<]+)<\/(?:\w+:)?ele>/i.exec(body)?.[1];
  if (raw == null) return null;
  const parsed = Number(raw.trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function parsePoints(xml: string): RawRoutePoint[] {
  const points: RawRoutePoint[] = [];
  const pointPattern =
    /<(?:\w+:)?(?:trkpt|rtept)\b([^>]*?)(?:\/\s*>|>([\s\S]*?)<\/(?:\w+:)?(?:trkpt|rtept)>)/gi;

  for (const match of xml.matchAll(pointPattern)) {
    const lat = Number(attribute(match[1], 'lat'));
    const lng = Number(attribute(match[1], 'lon'));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) continue;
    points.push({ elevation: elevation(match[2] ?? ''), lat, lng });
  }

  return points;
}

function evenlyReducePoints(points: readonly RawRoutePoint[]): RawRoutePoint[] {
  if (points.length <= MAX_ROUTE_POINTS) return [...points];

  const reduced: RawRoutePoint[] = [points[0]];
  const interiorSlots = MAX_ROUTE_POINTS - 2;
  for (let index = 1; index <= interiorSlots; index += 1) {
    const sourceIndex = Math.round((index * (points.length - 1)) / (interiorSlots + 1));
    reduced.push(points[sourceIndex]);
  }
  reduced.push(points.at(-1)!);
  return reduced;
}

function calculateBounds(points: readonly RawRoutePoint[]): RouteBounds {
  let minLat = points[0].lat;
  let maxLat = points[0].lat;
  let minLng = points[0].lng;
  let maxLng = points[0].lng;

  for (const point of points.slice(1)) {
    minLat = Math.min(minLat, point.lat);
    maxLat = Math.max(maxLat, point.lat);
    minLng = Math.min(minLng, point.lng);
    maxLng = Math.max(maxLng, point.lng);
  }

  return { maxLat, maxLng, minLat, minLng };
}

function calculateDistance(points: readonly RawRoutePoint[]): number {
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    total += distanceMeters(points[index - 1], points[index]);
  }
  return total;
}

function calculateElevationGain(points: readonly RawRoutePoint[]): number | null {
  const elevated = points.filter(
    (point): point is RawRoutePoint & { elevation: number } => point.elevation != null
  );
  if (elevated.length < 2) return null;

  let gain = 0;
  for (let index = 1; index < elevated.length; index += 1) {
    const delta = elevated[index].elevation - elevated[index - 1].elevation;
    if (delta > 1) gain += delta;
  }
  return gain;
}

export function parseGpxRoute(xml: string): ParsedGpxRoute {
  if (!xml.trim()) throw new Error('The selected GPX file is empty.');
  if (!/<(?:\w+:)?gpx\b/i.test(xml)) {
    throw new Error('The selected file is not a GPX document.');
  }

  const points = parsePoints(xml);
  if (points.length < 2) {
    throw new Error('The GPX file needs at least two valid track or route points.');
  }

  const distanceM = calculateDistance(points);
  if (!Number.isFinite(distanceM) || distanceM <= 0) {
    throw new Error('The GPX route has no measurable distance.');
  }

  const reduced = evenlyReducePoints(points);
  return {
    bounds: calculateBounds(points),
    distanceM,
    elevationGainM: calculateElevationGain(points),
    geometry: {
      coordinates: reduced.map((point) =>
        point.elevation == null
          ? ([point.lng, point.lat] as const)
          : ([point.lng, point.lat, point.elevation] as const)
      ),
      type: 'LineString',
    },
    name: routeName(xml),
    originalPointCount: points.length,
  };
}

export function routeGeometryToGpx(geometry: RouteLineString, name = '6am route'): string {
  if (geometry.coordinates.length < 2) {
    throw new Error('A GPX route needs at least two points.');
  }

  const trackPoints = geometry.coordinates
    .map(([longitude, latitude, elevationM]) => {
      const elevation = elevationM == null ? '' : `<ele>${Number(elevationM).toFixed(2)}</ele>`;
      return `      <trkpt lat="${latitude.toFixed(7)}" lon="${longitude.toFixed(7)}">${elevation}</trkpt>`;
    })
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="6am BKK" xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>${xmlEntityEncode(name.trim() || '6am route')}</name>
  </metadata>
  <trk>
    <name>${xmlEntityEncode(name.trim() || '6am route')}</name>
    <trkseg>
${trackPoints}
    </trkseg>
  </trk>
</gpx>
`;
}

export function formatRouteDistance(distanceM: number): string {
  if (distanceM < 1_000) return `${Math.round(distanceM)} M`;
  const kilometers = distanceM / 1_000;
  return `${kilometers < 10 ? kilometers.toFixed(1) : Math.round(kilometers)} KM`;
}

export function formatRouteElevation(elevationGainM: number | null): string {
  return elevationGainM == null ? '—' : `${Math.round(elevationGainM)} M`;
}

export function routePreviewPath(
  coordinates: readonly RouteCoordinate[],
  width: number,
  height: number,
  padding = 16
): string {
  if (coordinates.length < 2 || width <= padding * 2 || height <= padding * 2) return '';

  const averageLat =
    coordinates.reduce((sum, coordinate) => sum + coordinate[1], 0) / coordinates.length;
  const longitudeScale = Math.max(0.1, Math.cos(radians(averageLat)));
  const projected = coordinates.map(([lng, lat]) => ({ x: lng * longitudeScale, y: lat }));
  const xs = projected.map((point) => point.x);
  const ys = projected.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const sourceWidth = Math.max(maxX - minX, 1e-9);
  const sourceHeight = Math.max(maxY - minY, 1e-9);
  const availableWidth = width - padding * 2;
  const availableHeight = height - padding * 2;
  const scale = Math.min(availableWidth / sourceWidth, availableHeight / sourceHeight);
  const offsetX = padding + (availableWidth - sourceWidth * scale) / 2;
  const offsetY = padding + (availableHeight - sourceHeight * scale) / 2;

  return projected
    .map((point, index) => {
      const x = offsetX + (point.x - minX) * scale;
      const y = height - (offsetY + (point.y - minY) * scale);
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
}

export function routePreviewPathLength(path: string): number {
  const points = [...path.matchAll(/[ML]([\d.-]+),([\d.-]+)/g)].map((match) => ({
    x: Number(match[1]),
    y: Number(match[2]),
  }));
  let length = 0;
  for (let index = 1; index < points.length; index += 1) {
    length += Math.hypot(
      points[index].x - points[index - 1].x,
      points[index].y - points[index - 1].y
    );
  }
  return length;
}
